// Dependencies
import { FC, MouseEventHandler, ReactElement, ReactNode } from "react";
import { alpha, styled } from "@mui/material/styles";

// Components

// Icons
import { ReactComponent as GoogleIcon } from "~/assets/icons/GoogleIcon.svg";
import { ReactComponent as FacebookIcon } from "~/assets/icons/FacebookIcon.svg";

// styles
import { StyledLoadingButton } from "./Button.styled";

interface ButtonProps {
  children: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement> | undefined;
  variant?: "text" | "contained" | "outlined";
  type?: "button" | "submit" | "reset";
  icon?: ReactElement;
  disabled?: boolean;
  loading?: boolean;
  endIcon?: ReactNode;
  color?:
    | "inherit"
    | "error"
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | undefined;
}

const Button: FC<ButtonProps> = ({ children, variant, icon, ...props }) => {
  return (
    <StyledLoadingButton startIcon={icon} variant={variant} {...props}>
      {children}
    </StyledLoadingButton>
  );
};

// Custom buttons form Google and Facebook
const googleColor = "#EA4335";
const facebookColor = "#0E53BB";

const GoogleButtonStyle = styled(Button, { name: "Button" })({
  "&": {
    borderColor: googleColor,
    backgroundColor: "white",
    color: "black",
    "&:hover": {
      border: `1px solid ${googleColor} `,
      backgroundColor: alpha(googleColor, 0.1),
    },

    ".MuiTouchRipple-root span": {
      color: googleColor,
    },
  },
});

const FacebookButtonStyle = styled(Button, { name: "Button" })({
  "&": {
    borderColor: facebookColor,
    backgroundColor: "white",
    color: "black",
    "&:hover": {
      border: `1px solid ${facebookColor} `,
      backgroundColor: alpha(facebookColor, 0.1),
    },

    ".MuiTouchRipple-root span": {
      color: facebookColor,
    },
  },
});

const GoogleButton: FC<ButtonProps> = ({ children, ...props }) => {
  return (
    <GoogleButtonStyle {...props} variant={"outlined"} icon={<GoogleIcon />}>
      {children}
    </GoogleButtonStyle>
  );
};

const FacebookButton: FC<ButtonProps> = ({ children, ...props }) => {
  return (
    <FacebookButtonStyle
      {...props}
      variant={"outlined"}
      icon={<FacebookIcon />}
    >
      {children}
    </FacebookButtonStyle>
  );
};

const WarningButton: FC<ButtonProps> = ({ children, ...props }) => {
  return (
    <Button {...props} variant={"outlined"} color="error">
      {children}
    </Button>
  );
};

export { GoogleButton, FacebookButton, WarningButton };
export default Button;
